<script setup>
import { computed, onMounted, onBeforeUnmount, ref, watch } from "vue";
import { Search, ArrowDownToLine, ArrowUpFromLine, Radio } from "@lucide/vue";
import { api, listenToAccessLogs } from "../api";
import { useSessionStore } from "../stores/session";
import PageHeader from "../components/PageHeader.vue";
import TablePager from "../components/TablePager.vue";
import UiToast from "../ui/UiToast.vue";
import UiStudentAvatar from "../ui/UiStudentAvatar.vue";
const session = useSessionStore(),
  rows = ref([]),
  search = ref(""),
  decision = ref(""),
  page = ref(1),
  toast = ref(""),
  toastType = ref("success"),
  liveStatus = ref("connecting"),
  perPage = 20;
let streamController;
let reconnectTimer;
let refreshTimer;
const filtered = computed(() =>
    rows.value.filter(
      (r) =>
        (!decision.value || r.decision === decision.value) &&
        `${r.first_name || ""} ${r.last_name || ""} ${r.uid} ${r.device_name || ""} ${r.school_name || ""}`
          .toLocaleLowerCase("tr-TR")
          .includes(search.value.toLocaleLowerCase("tr-TR")),
    ),
  ),
  paged = computed(() =>
    filtered.value.slice((page.value - 1) * perPage, page.value * perPage),
  );
function show(m, t = "error") {
  toast.value = m;
  toastType.value = t;
  setTimeout(() => (toast.value = ""), 3000);
}
async function load() {
  try {
    const q = new URLSearchParams({ limit: "500" });
    if (session.selectedSchoolId) q.set("schoolId", session.selectedSchoolId);
    rows.value = (await api(`/api/logs?${q}`)).logs || [];
  } catch (e) {
    show(e.message);
  }
}
function scheduleLiveRefresh() {
  clearTimeout(refreshTimer);
  refreshTimer = setTimeout(load, 120);
}
function stopLiveStream() {
  clearTimeout(reconnectTimer);
  streamController?.abort();
  streamController = null;
}
function startLiveStream() {
  stopLiveStream();
  const controller = new AbortController();
  streamController = controller;
  liveStatus.value = "connecting";
  listenToAccessLogs({
    schoolId: session.selectedSchoolId,
    signal: controller.signal,
    onOpen: () => (liveStatus.value = "connected"),
    onLog: scheduleLiveRefresh,
  }).catch(() => {
    if (controller.signal.aborted) return;
    liveStatus.value = "reconnecting";
    reconnectTimer = setTimeout(startLiveStream, 2500);
  });
}
const dt = (v) =>
  new Intl.DateTimeFormat("tr-TR", {
    dateStyle: "short",
    timeStyle: "medium",
  }).format(new Date(v));
const reason = (r) =>
  ({
    authorized: "Yetkili geçiş",
    unknown_card: "Kayıtsız kart",
    card_blocked: "Kart blokeli",
    student_passive: "Öğrenci pasif",
    student_expired: "Süresi dolmuş",
    duplicate_scan: "Tekrarlı okutma",
  })[r] || r;
const refresh = () => load();
onMounted(() => {
  load();
  startLiveStream();
  window.addEventListener("lopiy:refresh", refresh);
});
watch(() => session.selectedSchoolId, startLiveStream);
onBeforeUnmount(() => {
  stopLiveStream();
  clearTimeout(refreshTimer);
  window.removeEventListener("lopiy:refresh", refresh);
});
</script>
<template>
  <PageHeader
    title="Geçiş logları"
    description="Erva Spor Okullarındaki tüm geçişleri öğrenci, cihaz ve sonuç bilgisiyle inceleyin."
  />
  <section class="panel overflow-hidden">
    <div class="toolbar">
      <div class="flex gap-2">
        <button
          v-for="item in [
            { v: '', l: 'Tümü' },
            { v: 'allowed', l: 'İzinli' },
            { v: 'denied', l: 'Reddedilen' },
          ]"
          :key="item.v"
          :class="[
            'h-9 rounded-md px-3 text-xs font-bold',
            decision === item.v
              ? 'bg-ink-900 text-white'
              : 'bg-slate-100 text-slate-600',
          ]"
          @click="
            decision = item.v;
            page = 1;
          "
        >
          {{ item.l }}
        </button>
      </div>
      <span
        class="inline-flex h-9 shrink-0 items-center gap-2 rounded-md border border-slate-200 bg-white px-3 text-xs font-bold text-slate-600"
        :title="
          liveStatus === 'connected'
            ? 'Yeni geçişler otomatik görüntülenir'
            : 'Canlı bağlantı yeniden kuruluyor'
        "
      >
        <Radio
          :class="[
            'size-3.5',
            liveStatus === 'connected'
              ? 'text-emerald-600'
              : 'animate-pulse text-amber-500',
          ]"
        />
        {{ liveStatus === "connected" ? "Canlı" : "Bağlanıyor" }}
      </span>
      <label class="relative w-full sm:w-80"
        ><Search class="absolute left-3 top-2.5 size-4 text-slate-400" /><input
          v-model="search"
          class="input pl-9"
          placeholder="Öğrenci, UID, okul veya cihaz ara"
      /></label>
    </div>
    <div class="overflow-x-auto">
      <table class="data-table">
        <thead>
          <tr>
            <th>Zaman</th>
            <th>Öğrenci</th>
            <th>Geçiş okulu</th>
            <th>Cihaz</th>
            <th>UID</th>
            <th>Yön</th>
            <th>Sonuç</th>
            <th>Açıklama</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in paged" :key="r.id">
            <td class="whitespace-nowrap">{{ dt(r.created_at) }}</td>
            <td>
              <div class="flex min-w-44 items-center gap-3">
                <UiStudentAvatar
                  :src="r.photo_path"
                  :first-name="r.first_name"
                  :last-name="r.last_name"
                />
                <div class="min-w-0">
                  <strong class="block truncate text-ink-900">{{
                    [r.first_name, r.last_name].filter(Boolean).join(" ") ||
                    "Bilinmeyen kart"
                  }}</strong>
                  <small
                    v-if="
                      r.student_school_name &&
                      r.student_school_name !== r.school_name
                    "
                    class="mt-0.5 block text-amber-600"
                    >Misafir: {{ r.student_school_name }}</small
                  >
                </div>
              </div>
            </td>
            <td>{{ r.school_name || "-" }}</td>
            <td>{{ r.device_name || "-" }}</td>
            <td class="font-mono text-xs">{{ r.uid }}</td>
            <td>
              <span class="inline-flex items-center gap-1 font-semibold"
                ><ArrowDownToLine
                  v-if="r.direction === 'entry'"
                  class="size-3.5 text-brand-600"
                /><ArrowUpFromLine v-else class="size-3.5 text-amber-600" />{{
                  r.direction === "entry" ? "Giriş" : "Çıkış"
                }}</span
              >
            </td>
            <td>
              <span
                :class="
                  r.decision === 'allowed' ? 'chip-success' : 'chip-danger'
                "
                >{{ r.decision === "allowed" ? "İzinli" : "Red" }}</span
              >
            </td>
            <td>{{ reason(r.reason) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <TablePager
      v-model:page="page"
      :total="filtered.length"
      :per-page="perPage"
    />
  </section>
  <UiToast :message="toast" :type="toastType" @close="toast = ''" />
</template>
